import type { ComputedRef } from 'vue'

import { computed, inject } from 'vue'

import type { Action, Permission, Subjects } from '../generated/abilities.generated'
import type { RbacController, UseCanReturn } from '../types/runtime'

import { runGates } from '../gates/runGates'
import { RBAC_KEY } from '../keys'

const requireController = (): RbacController => {
  const controller = inject(RBAC_KEY)

  if (!controller) {
    throw new Error('[rbac] useCan() must be called after the RBAC plugin is installed')
  }

  return controller
}

/**
 * The authorisation composable. Two ways to ask, mirroring the two fields of the
 * abilities payload:
 *
 *  - `can(action, subject)` — the CASL view, backed by the derived `rules`. This
 *    is the day-to-day check: the permission `pos.order.delete` is asked for as
 *    `can('delete', 'pos.order')`.
 *  - `has(permission)` — the canonical view, backed by `permissions[]` verbatim.
 *    Needed for what CASL cannot express: capability flags like `nova` and
 *    `login` have no action segment, so they never reach `rules` at all.
 *
 * Prefer `can()` wherever both work — it is the vocabulary the rest of the UI
 * reads in. Reach for `has()` when there is no action segment to split off, or
 * when you genuinely mean "holds this exact permission" rather than "may do this
 * thing".
 *
 * Both are reactive when read inside a `computed`/render: they touch `revision`,
 * so they re-evaluate as abilities load and change. A bare call outside a
 * reactive context is a point-in-time read.
 *
 * Subjects are opaque dotted strings, never parsed or re-cased, and carry no
 * hierarchy — `pos.settings` does not imply `pos.settings.configuration`. See
 * docs/ai/rules/rbac/permission-structure.md.
 */
export const useCan = (): UseCanReturn => {
  const controller = requireController()

  const can = (action: Action, subject: Subjects): boolean => {
    // Touch reactive sources so evaluation re-runs on change.
    void controller.revision.value
    void controller.online.value

    const gated = runGates(controller.gates, {
      action,
      subject,
      ability: controller.ability,
      meta: controller.meta.value,
      online: controller.online.value
    })

    if (gated !== undefined) {
      return gated
    }

    return controller.ability.can(action, subject)
  }

  const cannot = (action: Action, subject: Subjects): boolean => !can(action, subject)

  const has = (permission: Permission): boolean => {
    void controller.revision.value

    return controller.permissions.value.has(permission)
  }

  const hasAny = (permissions: Permission[]): boolean => permissions.some(permission => has(permission))

  const hasAll = (permissions: Permission[]): boolean => permissions.every(permission => has(permission))

  const computedCan = (action: Action, subject: Subjects): ComputedRef<boolean> => computed(() => can(action, subject))

  return {
    can,
    cannot,
    has,
    hasAny,
    hasAll,
    computedCan,
    ability: controller.ability,
    status: computed(() => controller.status.value)
  }
}
